import type { ISODate } from '@/types'

/** `3720` -> `"1:02:00"`, `315` -> `"5:15"`. */
export function formatDuration(totalSeconds: number): string {
  const safe = Math.max(0, Math.round(totalSeconds))
  const hours = Math.floor(safe / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  const seconds = safe % 60
  const mm = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes)
  return `${hours > 0 ? `${hours}:` : ''}${mm}:${String(seconds).padStart(2, '0')}`
}

/** `"5:15"` / `"5"` / `"315s"` -> seconds. Returns `null` when unparseable. */
export function parseDuration(input: string): number | null {
  const trimmed = input.trim()
  if (!trimmed) return null
  if (/^\d+s$/i.test(trimmed)) return Number(trimmed.slice(0, -1))
  const parts = trimmed.split(':').map((p) => p.trim())
  if (parts.some((p) => p === '' || !/^\d+$/.test(p))) return null
  if (parts.length === 1) return Number(parts[0]) * 60
  if (parts.length === 2) return Number(parts[0]) * 60 + Number(parts[1])
  if (parts.length === 3) return Number(parts[0]) * 3600 + Number(parts[1]) * 60 + Number(parts[2])
  return null
}

/** `"09:30"` + 3600s -> `"10:30"`. Wraps safely past midnight. */
export function addSecondsToClock(clock: string, seconds: number): string {
  const [h, m] = clock.split(':').map(Number)
  const total = (h * 3600 + m * 60 + seconds) % 86400
  const hh = Math.floor(total / 3600)
  const mm = Math.floor((total % 3600) / 60)
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

export function formatClock12h(clock: string): string {
  const [h, m] = clock.split(':').map(Number)
  const suffix = h >= 12 ? 'PM' : 'AM'
  const hour = h % 12 === 0 ? 12 : h % 12
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`
}

export function toISODate(date: Date): ISODate {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Parses `YYYY-MM-DD` as a *local* date, avoiding the UTC off-by-one. */
export function fromISODate(value: ISODate): Date {
  const [y, m, d] = value.split('-').map(Number)
  return new Date(y, (m ?? 1) - 1, d ?? 1)
}

export function formatDate(value: ISODate, style: 'short' | 'long' | 'weekday' = 'short'): string {
  const date = fromISODate(value)
  if (Number.isNaN(date.getTime())) return value
  const options: Intl.DateTimeFormatOptions =
    style === 'long'
      ? { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }
      : style === 'weekday'
        ? { weekday: 'short', month: 'short', day: 'numeric' }
        : { month: 'short', day: 'numeric' }
  return new Intl.DateTimeFormat(undefined, options).format(date)
}

/** "just now" / "4h ago" / "12 Mar" for the notification feed. */
export function formatRelative(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const diffSeconds = Math.round((Date.now() - then) / 1000)
  if (diffSeconds < 45) return 'just now'
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['minute', 60],
    ['hour', 3600],
    ['day', 86400],
    ['week', 604800],
  ]
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  for (let i = units.length - 1; i >= 0; i -= 1) {
    const [unit, secondsPerUnit] = units[i]
    if (Math.abs(diffSeconds) >= secondsPerUnit) {
      return formatter.format(-Math.round(diffSeconds / secondsPerUnit), unit)
    }
  }
  return formatter.format(-Math.round(diffSeconds / 60), 'minute')
}

/** Masks an API key for display: `mdv_live_a1b2…9f3c`. */
export function maskKey(key: string): string {
  if (key.length <= 12) return key
  return `${key.slice(0, 12)}…${key.slice(-4)}`
}
